"use client"

import * as React from "react"

import type { Customer } from "@workspace/model"

/**
 * Auth context seam (US-011, wired for real in US-028). The root layout's
 * `TenantShell` reads the session server-side ONCE (`getCurrentSession()`,
 * `@/lib/auth` — cookie-backed, never cached, see that file's doc comment)
 * and passes it down as `initialSession`; this provider just holds it for
 * Client Components (e.g. a future cart/checkout auth gate) to read via
 * `useAuth()`. It does NOT re-fetch or subscribe to changes — sign-in/out
 * happen via full-page Server Action redirects (`account/actions.ts`), which
 * naturally re-render this from a fresh server value on the next request.
 */
export interface AuthSessionValue {
  /** null when signed out. */
  session: Customer | null
}

const AuthContext = React.createContext<AuthSessionValue | null>(null)

export function AuthProvider({
  children,
  initialSession,
}: {
  children: React.ReactNode
  initialSession: Customer | null
}) {
  const value = React.useMemo<AuthSessionValue>(
    () => ({ session: initialSession }),
    [initialSession]
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthSessionValue {
  const ctx = React.useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return ctx
}
